import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { ToastContainer, toast } from 'react-toastify';
import PhoneVerification from './PhoneVerification';
import '../../../shared/styles/mySettingsStyles.css';

const axios = require('axios').default;

// https://stackoverflow.com/questions/46155/how-to-validate-an-email-address-in-javascript
function validateEmail(email) {
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}

const canSubmitForm = (user, originalUser) => {
  var baseConditions = (
    user
      && user.first_name
      && user.first_name.length > 0
      && user.last_name
      && user.last_name.length > 0
      && user.email
      && user.email.length > 0
      && validateEmail(user.email)
  );

  return (
    baseConditions
      && userHasChanges(originalUser, user)
  );
}

const userHasChanges = (originalUser, user) => {
  return (
    originalUser.first_name !== user.first_name
      || originalUser.last_name !== user.last_name
      || originalUser.email !== user.email
      || (originalUser.phone || '') !== (user.phone || '')
      || user.avatarFile
  );
}

const UploadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M14 10v2.667A1.333 1.333 0 0112.667 14H3.333A1.333 1.333 0 012 12.667V10M11.333 5.333L8 2m0 0L4.667 5.333M8 2v8"
          stroke="currentColor" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const UserSettings = ({
  csrfToken,
  originalUser,
  user,
  userChanged,
  updateUser,
  originalUserChanged,
  fileEl,
  fileElChanged,
  updateUserHomepage,
  updateUserTimezone,
  team,
  myMobileApps,
  availableTimezones,
  conductorWidget,
}) => {
  const [sentPasswordReset, setSentPasswordReset] = useState(false);
  const [conductorWidgetEnabled, setConductorWidgetEnabled] = useState(conductorWidget?.enabled);

  const toggleConductorWidget = (enabled) => {
    setConductorWidgetEnabled(enabled);
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    axios.patch(`/teams/${conductorWidget.teamId}/team_memberships/${conductorWidget.teamMembershipId}`, {
      team_membership: { conductor_widget_enabled: enabled }
    }).catch(() => {
      setConductorWidgetEnabled(!enabled);
      toast.error("Couldn't save your Conductor widget preference.");
    });
  };

  const sendResetPasswordEmail = () => {
    if(sentPasswordReset){
      return;
    }

    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    axios.post(`/users/password`, {
      user: { email: user.email }
    })
      .then(function (response) {
        setSentPasswordReset(true);

        toast.success("You should receive a password reset email soon.", {
          position: toast.POSITION.TOP_CENTER,
          draggable: false,
          closeOnClick: false,
          autoClose: 5000,
          hideProgressBar: true
        });
      })
      .catch(function (error) {
        toast.error("We could not send you a password reset email. Please try again.", {
          position: toast.POSITION.TOP_CENTER,
          draggable: false,
          closeOnClick: true,
          autoClose: 5000,
          hideProgressBar: true
        });
      });
  }

  return (
  <React.Fragment>
    <ToastContainer icon={false} />

    {/* ---- My profile ---- */}
    <section className="settings-card" data-settings-section id="my-profile">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if(!canSubmitForm(user, originalUser)){ return false; }
          updateUser(csrfToken, user);
        }}
      >
        <div className="settings-card-header">
          <h2 className="settings-card-title">My profile</h2>
          <p className="settings-card-desc">Update your photo and personal details.</p>
        </div>
        <div className="settings-card-body">
          <div className="settings-app-row" style={{ marginBottom: "var(--spacing-3xl, 24px)" }}>
            <img
              src={user.preview_url || user.avatar_thumb_url}
              className="settings-avatar"
              alt=""
            />
            <div>
              <input
                ref={el => {
                  if(el && Object.keys(fileEl).length === 0){
                    fileElChanged(el);
                  }
                }}
                onChange={(e) => {
                  var file = e.target.files[0];
                  if(file){
                    var updated = Object.assign({}, user, {
                      avatarFile: file,
                      preview_url: URL.createObjectURL(file)
                    });
                    userChanged(updated);
                  }
                  e.target.value = null;
                }}
                accept="image/*"
                type="file"
                style={{ display: "none" }}
              />
              <div className="d-flex align-items-center" style={{ gap: "var(--spacing-lg, 12px)" }}>
                <button
                  type="button"
                  className="settings-btn settings-btn-secondary"
                  onClick={(e) => { e.preventDefault(); fileEl.click(); }}
                >
                  <UploadIcon />
                  Choose photo
                </button>
                {user.avatarFile ? (
                  <button
                    type="button"
                    className="settings-btn settings-btn-secondary"
                    onClick={(e) => {
                      e.preventDefault();
                      var updated = Object.assign({}, user);
                      delete updated.avatarFile;
                      delete updated.preview_url;
                      userChanged(updated);
                    }}
                  >
                    Remove
                  </button>
                ) : null}
              </div>
              <p className="settings-helper">Images must be at least 200 × 200px.</p>
            </div>
          </div>

          <div className="settings-field-grid">
            <div className="settings-field">
              <label className="settings-label" htmlFor="user-first-name">First name</label>
              <input
                type="text"
                className="form-control settings-input"
                id="user-first-name"
                value={user.first_name || ""}
                onChange={(e) => userChanged(Object.assign({}, user, { first_name: e.target.value }))}
              />
            </div>
            <div className="settings-field">
              <label className="settings-label" htmlFor="user-last-name">Last name</label>
              <input
                type="text"
                className="form-control settings-input"
                id="user-last-name"
                value={user.last_name || ""}
                onChange={(e) => userChanged(Object.assign({}, user, { last_name: e.target.value }))}
              />
            </div>
            <div className="settings-field settings-field--full">
              <label className="settings-label" htmlFor="user-phone">Mobile phone</label>
              <input
                type="tel"
                className="form-control settings-input"
                id="user-phone"
                value={user.phone || ""}
                onChange={(e) => userChanged(Object.assign({}, user, { phone: e.target.value }))}
              />
              {team && <p className="settings-helper">Used for text messages from your team's automations.</p>}
              {originalUser.phone && originalUser.phone === user.phone && (
                <PhoneVerification
                  key={originalUser.phone}
                  csrfToken={csrfToken}
                  phone={originalUser.phone}
                  verified={Boolean(originalUser.phone_verified)}
                  codeSent={originalUser.phone_code_sent}
                  unsentReason={originalUser.phone_code_unsent_reason}
                  notice={originalUser.phone_code_notice}
                  onConfirmed={(data) => {
                    const confirmed = { phone_verified: data.phone_verified, phone_verified_at: data.phone_verified_at };
                    userChanged(Object.assign({}, user, confirmed));
                    originalUserChanged(Object.assign({}, originalUser, confirmed));
                  }}
                />
              )}
            </div>
            <div className="settings-field settings-field--full">
              <label className="settings-label" htmlFor="user-email">Email</label>
              <input
                type="email"
                className="form-control settings-input"
                id="user-email"
                value={user.email || ""}
                onChange={(e) => userChanged(Object.assign({}, user, { email: e.target.value }))}
              />
              {user.unconfirmed_email ? (
                <p className="settings-helper">
                  <strong>Email address update in progress.</strong> Please check your inbox to confirm the email update.
                </p>
              ) : null}
            </div>
          </div>
        </div>
        <div className="settings-card-footer">
          <button type="submit" className="settings-btn" disabled={!canSubmitForm(user, originalUser)}>
            Update profile
          </button>
        </div>
      </form>
    </section>

    {/* ---- Password ---- */}
    <section className="settings-card" data-settings-section id="password">
      <div className="settings-card-header settings-card-header--row">
        <div>
          <h2 className="settings-card-title">Password</h2>
          <p className="settings-card-desc">
            Forgot your password or need to create one? We'll email you a reset link.
          </p>
        </div>
        <div className="d-flex align-items-center" style={{ gap: "var(--spacing-lg, 12px)" }}>
          <button
            type="button"
            className="settings-btn settings-btn-secondary"
            disabled={!user.email || user.email.length === 0 || sentPasswordReset}
            onClick={(e) => { e.preventDefault(); sendResetPasswordEmail(); }}
          >
            Reset password
          </button>
          {sentPasswordReset ? <span className="settings-helper" style={{ margin: 0 }}>Reset email sent.</span> : null}
        </div>
      </div>
    </section>

    {/* ---- Preferences ---- */}
    <section className="settings-card" data-settings-section id="preferences">
      <div className="settings-card-header">
        <h2 className="settings-card-title">Preferences</h2>
        <p className="settings-card-desc">{team?.setup_complete ? "Choose your default homepage and local timezone." : "Choose your local timezone."}</p>
      </div>
      <div className="settings-card-body">
        <div className="settings-field-grid">
          {team?.setup_complete ? (
            <div className="settings-field">
              <label className="settings-label" htmlFor="user-homepage">Homepage</label>
              <select
                className="form-control settings-input"
                id="user-homepage"
                value={user.homepage || ""}
                onChange={(e) => {
                  var updated = Object.assign({}, user, { homepage: e.target.value });
                  userChanged(updated);
                  updateUserHomepage(csrfToken, updated);
                }}
              >
                <option value="My Tickets">My Tickets</option>
                <option value="Venue Manager">Venue Manager</option>
                <option value="Support">Help Page</option>
              </select>
              <p className="settings-helper">Where you land when you log in to Opendate.</p>
            </div>
          ) : null}
          <div className="settings-field">
            <label className="settings-label" htmlFor="user-timezone">Timezone</label>
            <select
              className="form-control settings-input"
              id="user-timezone"
              value={user.time_zone || ""}
              onChange={(e) => {
                var updated = Object.assign({}, user, { time_zone: e.target.value });
                userChanged(updated);
                updateUserTimezone(csrfToken, updated);
              }}
            >
              { availableTimezones.map(timezone => (
                <option value={timezone} key={timezone}>{timezone}</option>
              ))}
            </select>
            <p className="settings-helper">Used for event times and email summaries.</p>
          </div>
        </div>
        {conductorWidget ? (
          <div className="settings-pref-row">
            <div>
              <label className="settings-pref-name" htmlFor="conductor-widget-enabled">Conductor widget</label>
              <p className="settings-pref-desc">Show the floating Ask Conductor shortcut. Conductor is always available from the left nav.</p>
            </div>
            <label className="settings-toggle">
              <input
                type="checkbox"
                id="conductor-widget-enabled"
                checked={conductorWidgetEnabled}
                onChange={(e) => toggleConductorWidget(e.target.checked)}
              />
              <span className="settings-toggle-track" />
            </label>
          </div>
        ) : null}
      </div>
    </section>

    {/* ---- Mobile apps ---- */}
    {myMobileApps.length > 0 ? (
      <section className="settings-card" data-settings-section id="mobile-apps">
        <div className="settings-card-header">
          <h2 className="settings-card-title">Mobile apps</h2>
          <p className="settings-card-desc">
            Apps for your favorite venues to manage tickets and explore upcoming shows.
          </p>
        </div>
        <div className="settings-card-body">
          <div className="settings-field-grid">
            {myMobileApps.sort((a, b) => a.name.localeCompare(b.name)).map((mobileApp, index) => (
              <div className="settings-app-row" key={index}>
                <img src={mobileApp.icon_thumb_url} className="settings-app-icon" alt="" />
                <div>
                  <p className="settings-app-name">{mobileApp.name}</p>
                  {mobileApp.human_registered_at ? (
                    <p className="settings-app-meta">Installed on {mobileApp.human_registered_at}</p>
                  ) : (
                    <div className="mt-1 d-flex" style={{ gap: "var(--spacing-md, 8px)" }}>
                      {mobileApp.app_store_url && mobileApp.app_store_url.length > 0 ? (
                        <a href={mobileApp.app_store_url} target="_blank" rel="noreferrer">
                          <img src="/app_store.png" style={{ width: "96px", height: "32px" }} alt="Download on the App Store" />
                        </a>
                      ) : null}
                      {mobileApp.google_play_url && mobileApp.google_play_url.length > 0 ? (
                        <a href={mobileApp.google_play_url} target="_blank" rel="noreferrer">
                          <img src="/google_play.png" style={{ width: "110.22px", height: "32px" }} alt="Get it on Google Play" />
                        </a>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    ) : null}
  </React.Fragment>
  );
};

UserSettings.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  originalUser: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  userChanged: PropTypes.func.isRequired,
  updateUser: PropTypes.func.isRequired,
  originalUserChanged: PropTypes.func.isRequired,
  fileEl: PropTypes.object,
  fileElChanged: PropTypes.func.isRequired,
  updateUserHomepage: PropTypes.func.isRequired,
  updateUserTimezone: PropTypes.func.isRequired,
  team: PropTypes.object,
  myMobileApps: PropTypes.array,
  availableTimezones: PropTypes.array,
  conductorWidget: PropTypes.shape({
    teamId: PropTypes.number.isRequired,
    teamMembershipId: PropTypes.number.isRequired,
    enabled: PropTypes.bool.isRequired,
  }),
};

UserSettings.defaultProps = {
  team: null,
};

export default UserSettings;
